/**
 * المرحلة 1.4 — the visit page's rules, as the screens preview them.
 *
 * The same answers as Backend/lib/encounters.js for what a screen shows
 * before it asks the server: the SOAP sections still empty, the readings out
 * of range, what keeps a visit open, a sick leave's last day, a verify code
 * typed by hand. Tests/suites/frontend/p14-prints.test.mjs runs both sides on
 * the same cases. The server decides; these only keep a button honest.
 */

import { SOAP_SECTIONS } from './clinicalPolicy';

export { SOAP_SECTIONS };

/* ── verify codes ───────────────────────────────────────────────────────── */

export const TOKEN_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const TOKEN_LENGTH = 12;
const TOKEN_KINDS = Object.freeze({ C: 'certificate', R: 'referral', V: 'visit_report' });

/** What a person typed (dashes, spaces, lower case) as the code it should be; null when it cannot be one. */
export function normaliseToken(input) {
  const plain = String(input || '').toUpperCase().replace(/[\s-]+/g, '');
  if (plain.length !== TOKEN_LENGTH) return null;
  if (!TOKEN_KINDS[plain[0]]) return null;
  for (const ch of plain.slice(1)) if (!TOKEN_ALPHABET.includes(ch)) return null;
  return plain;
}

export const kindOfToken = (token) => {
  const t = normaliseToken(token);
  return t ? TOKEN_KINDS[t[0]] : null;
};

/** As printed: three groups of four. */
export const groupToken = (token) => (token ? String(token).replace(/(.{4})(?=.)/g, '$1-') : '');

/* ── SOAP ───────────────────────────────────────────────────────────────── */

const SOAP_MAX = 8000;

export function cleanSoap(body = {}) {
  const out = {};
  for (const k of SOAP_SECTIONS) out[k] = String(body[k] === undefined || body[k] === null ? '' : body[k]).trim().slice(0, SOAP_MAX);
  return out;
}

export const soapEmpty = (soap = {}) => SOAP_SECTIONS.every((k) => !String(soap[k] || '').trim());

export function soapMissing(soap = {}, required = []) {
  return SOAP_SECTIONS.filter((k) => required.includes(k) && !String(soap[k] || '').trim());
}

/** Has the draft moved away from what was saved? */
export function soapDirty(saved = {}, draft = {}) {
  return SOAP_SECTIONS.some((k) => String(saved[k] || '').trim() !== String(draft[k] || '').trim());
}

/* ── vitals ─────────────────────────────────────────────────────────────── */

export const VITAL_KINDS = Object.freeze({
  bp: ['bp_sys', 'bp_dia'],
  hr: ['hr'],
  temp: ['temp'],
  spo2: ['spo2'],
  rr: ['rr'],
  glucose: ['glucose'],
  weight: ['weight'],
  height: ['height'],
});
export const FORM_KINDS = Object.freeze(Object.values(VITAL_KINDS).flat());

/** What a reading must fall in to be kept (Backend/lib/observations.js KINDS). */
export const KIND_RANGES = Object.freeze({
  bp_sys: { min: 50, max: 260, unit: 'mmHg' },
  bp_dia: { min: 30, max: 160, unit: 'mmHg' },
  hr: { min: 20, max: 250, unit: 'bpm' },
  temp: { min: 30, max: 45, unit: '°C' },
  spo2: { min: 50, max: 100, unit: '%' },
  rr: { min: 4, max: 80, unit: '/min' },
  glucose: { min: 10, max: 1000, unit: 'mg/dL' },
  weight: { min: 0.3, max: 400, unit: 'kg' },
  height: { min: 20, max: 250, unit: 'cm' },
});

/** The adult normal ranges a reading is flagged outside of (Backend/lib/encounters.js NORMAL_RANGES). */
export const NORMAL_RANGES = Object.freeze({
  bp_sys: { low: 90, high: 140 },
  bp_dia: { low: 60, high: 90 },
  hr: { low: 60, high: 100 },
  temp: { low: 36, high: 37.5 },
  spo2: { low: 95, high: 100 },
  rr: { low: 12, high: 20 },
  glucose: { low: 70, high: 180 },
});

/** Arabic-Indic digits and the Arabic decimal mark, as a number. */
export function toNumber(value) {
  const ascii = String(value === undefined || value === null ? '' : value)
    .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
    .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
    .replace(/[٫,]/g, '.')
    .trim();
  if (!ascii) return null;
  const n = Number(ascii);
  return Number.isFinite(n) ? n : NaN;
}

/** An empty readings form. */
export const blankVitals = () => Object.fromEntries(FORM_KINDS.map((k) => [k, '']));

/**
 * The form as the server takes it: the readings typed, as numbers. Returns
 * `{ readings, invalid }` — the kinds typed that are not a reading in range.
 */
export function vitalsPayload(form = {}) {
  const readings = {};
  const invalid = [];
  for (const kind of FORM_KINDS) {
    const n = toNumber(form[kind]);
    if (n === null) continue;
    const range = KIND_RANGES[kind];
    if (!Number.isFinite(n) || n < range.min || n > range.max) invalid.push(kind);
    else readings[kind] = Math.round(n * 100) / 100;
  }
  return { readings, invalid };
}

export function alertsFor(rows = [], policy = { alerts: 'standard' }) {
  if (!policy || policy.alerts === 'off') return [];
  const out = [];
  for (const r of rows) {
    const range = NORMAL_RANGES[r.kind];
    if (!range) continue;
    const v = Number(r.value);
    if (v < range.low) out.push({ kind: r.kind, value: v, level: 'low', range });
    else if (v > range.high) out.push({ kind: r.kind, value: v, level: 'high', range });
  }
  return out;
}

export function vitalsMissing(kindsTaken = [], required = []) {
  const taken = new Set(kindsTaken);
  return required.filter((group) => !(VITAL_KINDS[group] || []).every((kind) => taken.has(kind)));
}

/* ── closing ────────────────────────────────────────────────────────────── */

export function closeProblems({ soap = {}, diagnoses = [], vitalKinds = [] } = {}, policies = {}) {
  const note = policies.clinicalNote || { format: 'both', required: [] };
  const missing = {
    soap: note.format === 'free' ? [] : soapMissing(soap, note.required || []),
    vitals: vitalsMissing(vitalKinds, (policies.visitVitals && policies.visitVitals.required) || []),
    icd10: Boolean(policies.icd10 && policies.icd10.mode === 'required' && !diagnoses.some((d) => d.primary && d.code)),
  };
  return missing.soap.length || missing.vitals.length || missing.icd10 ? missing : null;
}

/** A visit as its page reads it: the problems the server sent, the readings' kinds. */
export function visitProblems(view) {
  if (!view || view.status !== 'open') return null;
  return closeProblems({ soap: view.soap, diagnoses: view.diagnoses, vitalKinds: (view.vitals || []).map((v) => v.kind) }, view.policy || {});
}

/* ── documents ──────────────────────────────────────────────────────────── */

const DAY_MS = 86400000;
const isDay = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || '')) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));

/** The last day of a leave of `days` from `start`, both inclusive. */
export function endOf(start, days) {
  const d = new Date(Date.parse(`${start}T00:00:00Z`) + (Math.max(1, days) - 1) * DAY_MS);
  return d.toISOString().slice(0, 10);
}

/** What keeps a certificate form from being sent, as a code the screen words; null when it may go. */
export function certificateProblem(form = {}, policy = {}) {
  const kinds = policy.kinds || [];
  if (!kinds.includes(form.kind)) return 'kind';
  if (form.kind === 'sick_leave') {
    const days = Number(toNumber(form.days));
    if (!Number.isInteger(days) || days < 1 || days > (policy.maxDays || 14)) return 'days';
    if (form.startDate && !isDay(form.startDate)) return 'date';
  }
  if (form.kind === 'report' && !String(form.body || '').trim()) return 'body';
  return null;
}

/** A diagnosis's name in the reader's language, with its code in front when it has one. */
export function diagnosisLabel(d, lang = 'ar', { withCode = true } = {}) {
  if (!d) return '';
  const name = (lang === 'en' ? d.textEn || d.textAr : d.textAr || d.textEn) || '';
  return withCode && d.code ? `${d.code} — ${name}` : name;
}

/** The visit's primary diagnosis, or its first. */
export const primaryOf = (diagnoses = []) => diagnoses.find((d) => d.primary) || diagnoses[0] || null;
